import { useCallback, useMemo } from 'react';
import { useCascadingSelectOptions } from './useRiskCalculatorData';
import { formatCascadingOption } from '../utils/transformers';

/**
 * Custom hook for fetching cascading select options using React Query
 *
 * Features:
 * - Uses React Query for automatic caching and deduplication
 * - Built-in retry logic with exponential backoff
 * - Shows warnings when value not in options (instead of auto-clearing)
 * - Supports custom option formatters
 *
 * Note: "Show all" mode with debounced API search is handled directly
 * in UnifiedCascadingSelect component using lodash/debounce pattern.
 *
 * @param {Object} options - Configuration options
 * @param {string} options.step - Step type: 'treatment_type', 'application_technique', 'products', 'units'
 * @param {Array} options.cultures - Selected culture IDs
 * @param {string|null} options.treatmentType - Selected treatment type ID
 * @param {string|null} options.applicationTechnique - Selected application technique ID
 * @param {boolean} [options.enabled=true] - Whether to fetch options
 * @param {Function} [options.formatOption] - Custom option formatter
 * @returns {Object} { options, loading, error, hasNoResults, retry }
 */
export function useCascadingOptions({
  step,
  cultures = [],
  treatmentType = null,
  applicationTechnique = null,
  productId = null,
  enabled = true,
  formatOption,
}) {
  // Check if required dependencies are met for this step
  const hasDependencies = useMemo(() => {
    if (!cultures || cultures.length === 0) return false;

    switch (step) {
      case 'treatment_type':
        return true; // Only needs cultures
      case 'application_technique':
        return !!treatmentType;
      case 'products':
      case 'units':
        return !!treatmentType && !!applicationTechnique;
      default:
        return false;
    }
  }, [step, cultures, treatmentType, applicationTechnique]);

  // Use React Query hook for aggregated data fetching
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useCascadingSelectOptions(
    {
      step,
      cultures,
      treatmentType,
      applicationTechnique,
      productId,
    },
    {
      enabled: enabled && hasDependencies,
    }
  );

  // Format options using provided formatter or default
  const formatter = useCallback(
    (opt) => (formatOption ? formatOption(opt) : formatCascadingOption(opt, step)),
    [formatOption, step]
  );

  // Format the options
  const options = useMemo(() => {
    return (data?.options || []).map(formatter);
  }, [data, formatter]);

  // Check if query is enabled
  const isEnabled = enabled && hasDependencies;

  // Only show "no results" when query was enabled, finished loading, AND returned empty
  const hasNoResults = isEnabled && !isLoading && options.length === 0;

  // Manual retry function
  const retry = useCallback(() => {
    refetch?.();
  }, [refetch]);

  return {
    options,
    loading: isLoading,
    error: error?.response?.data?.error || error?.message || null,
    hasNoResults,
    retry,
  };
}

export default useCascadingOptions;
